/**
 * KraftStories — Section Analytics
 * ---------------------------------------------------------------------------
 * GA4 knows which *page* someone engaged with. It has never known which
 * *section*. That gap is why a redesign can lift a page's engagement rate
 * without anyone being able to say which of the twelve blocks on it did the
 * work.
 *
 * This adds three events, scoped to the design-system sections only:
 *
 *   section_view      the section held at least half the viewport for a second
 *   section_read      its bottom edge cleared the viewport — the visitor got
 *                     all the way through it rather than scrolling past
 *   section_cta_click a button, modal trigger or link inside it was clicked
 *   secdwell_<type>_10 / _30   the section accumulated 10s / 30s of actual
 *                     on-screen time (visible ≥ half-viewport, tab focused).
 *                     The TYPE and THRESHOLD live in the event name — same
 *                     Windsor convention as ab-engine and scroll-depth.js —
 *                     so "which section holds attention longest" is queryable
 *                     with no custom-dimension registration. Hyphens become
 *                     underscores for GA4-safe names (split-hero →
 *                     secdwell_split_hero_10). Fires once each per pageview.
 *
 * Every event carries section_type, section_position and page_template, so a
 * section's performance can be compared across the pages it appears on, and
 * pages can be compared by what they are built from.
 *
 * Routing: everything goes through window.__kraftGa4Mirror, which
 * analytics-events.js owns. That is deliberate — the GTM double-count rule
 * lives in exactly one place, and this file must not become a second one.
 * Must therefore load AFTER analytics-events.js, as header-analytics.js does.
 *
 * Cost control: only the sections in TRACKED are watched, each fires each
 * event at most once per page, and the observers disconnect as they go. A
 * typical page produces well under twenty events for a full read.
 */
(function () {
  'use strict';

  /** Design-system sections. The value is what lands in GA4 as section_type. */
  var TRACKED = {
    'kshero-section': 'split-hero',
    'section-hub-hero': 'hub-hero',
    'section-team-activities-local': 'team-activities',
    'section-photo-principles': 'photo-principles',
    'section-statement-band': 'statement-band',
    'section-format-cards': 'format-cards',
    'section-occasion-selector': 'occasion-selector',
    'section-promise-ledger': 'promise-ledger',
    'section-location-bestsellers-tabs': 'bestsellers-tabs',
    'section-run-of-show': 'run-of-show',
    'section-design-features-logos': 'logo-bar',
    'section-signature-experience': 'signature-experience',
    'section-content-tabs': 'content-tabs'
  };

  var VIEW_RATIO = 0.5;   // half the section on screen…
  var VIEW_DWELL = 1000;  // …held for a second, so a fast scroll past is not a view
  var MAX_EVENTS = 60;    // hard backstop against a runaway page

  if (!('IntersectionObserver' in window)) return;

  var sent = 0;
  function emit(name, params) {
    if (sent >= MAX_EVENTS) return;
    sent++;
    if (typeof window.__kraftGa4Mirror === 'function') {
      window.__kraftGa4Mirror(name, params);
    } else if (typeof window.gtag === 'function') {
      // analytics-events.js missing or still loading: gtag direct is better
      // than losing the event, and a duplicate is impossible because the
      // helper is the only other path.
      window.gtag('event', name, params);
    }
  }

  function pageContext() {
    return {
      page_template: window.__kraft_page_template || '',
      page_category: window.__kraft_page_category || '',
      page_location_city: window.__kraft_page_location_city || ''
    };
  }

  function typeOf(el) {
    var list = el.classList;
    for (var i = 0; i < list.length; i++) {
      if (TRACKED[list[i]]) return TRACKED[list[i]];
    }
    return null;
  }

  /** Trimmed, GA4-safe label for whatever was clicked. */
  function labelOf(node) {
    var t = (node.getAttribute('aria-label') || node.textContent || '').replace(/\s+/g, ' ').trim();
    return t.length > 90 ? t.slice(0, 90) : t;
  }

  function init() {
    var wrappers = document.querySelectorAll('.shopify-section');
    var tracked = [];

    for (var i = 0; i < wrappers.length; i++) {
      var type = typeOf(wrappers[i]);
      if (type) tracked.push({ el: wrappers[i], type: type, pos: tracked.length + 1 });
    }
    if (!tracked.length) return;

    var ctx = pageContext();
    var byEl = new WeakMap();
    tracked.forEach(function (s) { byEl.set(s.el, s); });

    /* ---- section_view --------------------------------------------------- */
    var timers = new WeakMap();
    var viewObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var s = byEl.get(e.target);
        if (!s || s.viewed) return;
        if (e.isIntersecting && e.intersectionRatio >= VIEW_RATIO) {
          if (timers.has(e.target)) return;
          timers.set(e.target, setTimeout(function () {
            if (s.viewed) return;
            s.viewed = true;
            viewObs.unobserve(e.target);
            emit('section_view', Object.assign({
              section_type: s.type,
              section_position: s.pos,
              section_id: e.target.id || ''
            }, ctx));
          }, VIEW_DWELL));
        } else {
          clearTimeout(timers.get(e.target));
          timers['delete'](e.target);
        }
      });
    }, { threshold: [VIEW_RATIO] });

    /* ---- section_read ---------------------------------------------------
       Fires when the section's bottom edge has passed above the viewport
       bottom, i.e. the visitor scrolled through the whole thing. A section
       taller than the viewport that is simply skipped never triggers it. */
    var readObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var s = byEl.get(e.target);
        if (!s || s.read || !s.viewed) return;
        var floor = e.rootBounds ? e.rootBounds.bottom : window.innerHeight;
        if (!e.isIntersecting && e.boundingClientRect.bottom < floor) {
          s.read = true;
          readObs.unobserve(e.target);
          emit('section_read', Object.assign({
            section_type: s.type,
            section_position: s.pos
          }, ctx));
        }
      });
    }, { threshold: [0] });

    tracked.forEach(function (s) { viewObs.observe(s.el); readObs.observe(s.el); });

    /* ---- secdwell_<type>_10 / _30 ---------------------------------------
       Accumulated VISIBLE time per section: the clock runs only while the
       section holds ≥ VIEW_RATIO of itself on screen and the tab is focused,
       pauses on scroll-away or tab switch, and each threshold fires at most
       once per pageview. This answers "which section do people actually spend
       time on" — something view/read counts cannot. */
    var DWELL_THRESHOLDS = [10, 30]; // seconds
    function dwellName(type, t) { return 'secdwell_' + type.replace(/-/g, '_') + '_' + t; }

    function dwellState(s) {
      if (!s.dw) s.dw = { acc: 0, since: null, fired: {}, timers: {} };
      return s.dw;
    }
    function dwellFire(s, t) {
      var d = dwellState(s);
      if (d.fired[t]) return;
      d.fired[t] = true;
      emit(dwellName(s.type, t), Object.assign({
        section_type: s.type,
        section_position: s.pos,
        dwell_seconds: t
      }, ctx));
    }
    function dwellStart(s) {
      var d = dwellState(s);
      if (d.since !== null) return;
      d.since = Date.now();
      DWELL_THRESHOLDS.forEach(function (t) {
        if (d.fired[t]) return;
        var remaining = t * 1000 - d.acc;
        if (remaining <= 0) { dwellFire(s, t); return; }
        d.timers[t] = setTimeout(function () { dwellFire(s, t); }, remaining);
      });
    }
    function dwellStop(s) {
      var d = dwellState(s);
      if (d.since === null) return;
      d.acc += Date.now() - d.since;
      d.since = null;
      DWELL_THRESHOLDS.forEach(function (t) {
        clearTimeout(d.timers[t]);
        delete d.timers[t];
      });
      if (d.fired[30]) dwellObs.unobserve(s.el);
    }

    var dwellObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var s = byEl.get(e.target);
        if (!s) return;
        if (e.isIntersecting && e.intersectionRatio >= VIEW_RATIO && !document.hidden) {
          dwellStart(s);
        } else {
          dwellStop(s);
        }
      });
    }, { threshold: [VIEW_RATIO] });
    tracked.forEach(function (s) { dwellObs.observe(s.el); });

    document.addEventListener('visibilitychange', function () {
      tracked.forEach(function (s) {
        if (document.hidden) { dwellStop(s); }
        /* on return to the tab the IntersectionObserver re-reports current
           entries only on change; nudge visible sections back on manually */
        else if (s.dw && !s.dw.fired[30]) {
          var r = s.el.getBoundingClientRect();
          var vh = window.innerHeight || document.documentElement.clientHeight;
          var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
          if (visible > 0 && visible >= r.height * VIEW_RATIO) dwellStart(s);
        }
      });
    });

    /* ---- section_cta_click ---------------------------------------------- */
    document.addEventListener('click', function (ev) {
      var node = ev.target && ev.target.closest && ev.target.closest(
        '.jx-btn a, [data-modal-trigger], a[href], button[data-cta]');
      if (!node) return;
      var wrap = node.closest('.shopify-section');
      if (!wrap) return;
      var s = byEl.get(wrap);
      if (!s) return;

      var href = node.getAttribute('href') || '';
      emit('section_cta_click', Object.assign({
        section_type: s.type,
        section_position: s.pos,
        cta_label: labelOf(node),
        cta_href: href.indexOf('javascript:') === 0 ? '' : href.slice(0, 100),
        cta_kind: node.hasAttribute('data-modal-trigger') ? 'modal'
                : (node.closest('.jx-btn') ? 'button' : 'link'),
        modal_id: node.getAttribute('data-modal-trigger') || ''
      }, ctx));
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
